/**
 * TEMPORARY, for #120 (cost of saving a 50,000-event playback queue on Samsung/Tizen).
 * Remove together with seek-debug.ts.
 *
 * With ?debug=queue in the player URL: builds 50,000 test events shaped like the real
 * queue, saves them under their own IndexedDB key (the real queue is never touched and
 * nothing is sent to the server), then adds one event and saves again every 10 s, 30 times.
 * Each save copies the events the way PlaybackEventBuffer does. The overlay shows how long
 * each save blocks the main thread (copy + IndexedDB put, which is what can make a wall
 * stutter) and the total time until the write is done. At the end it shows the median
 * and max and deletes the test data.
 */
import { META_STORE, openPlayerDatabase } from './idb/database.js';
import { idbTransactionDone } from './idb/request.js';
import { debugFlagEnabled, showDebugLine } from './seek-debug.js';
const DEBUG_KEY = 'playbackEventsDebug';
const EVENT_COUNT = 50_000;
const SAVE_EVERY_MS = 10_000;
const SAVES = 30;
let started = false;
function testUuid() {
    const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20)}`;
}
function testEvent(i) {
    return {
        clientEventId: testUuid(),
        eventType: i % 2 === 0 ? 'play_start' : 'play_end',
        mediaAssetId: testUuid(),
        playlistId: testUuid(),
        playlistVersion: 7,
        itemPosition: i % 5,
        playedAt: new Date(Date.now() - (EVENT_COUNT - i) * 10_000).toISOString(),
        ...(i % 2 === 1 ? { durationMs: 10_000 } : {}),
    };
}
function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
async function saveOnce(db, queueId, events) {
    const t0 = performance.now();
    // Same work as PlaybackEventBuffer.persist(): copy every event, then one put.
    const snapshot = {
        version: 1,
        queueId,
        droppedEvents: 0,
        acknowledgedDroppedEvents: 0,
        events: events.map((event) => ({ ...event })),
    };
    const tx = db.transaction(META_STORE, 'readwrite');
    const done = idbTransactionDone(tx);
    tx.objectStore(META_STORE).put({ key: DEBUG_KEY, value: snapshot });
    const blockedMs = performance.now() - t0;
    await done;
    return { blockedMs, totalMs: performance.now() - t0 };
}
async function deleteDebugData(db) {
    const tx = db.transaction(META_STORE, 'readwrite');
    const done = idbTransactionDone(tx);
    tx.objectStore(META_STORE).delete(DEBUG_KEY);
    await done;
}
async function run() {
    const db = await openPlayerDatabase();
    // A reload mid-test would leave the last run's ~20 MB behind: clear it before starting.
    await deleteDebugData(db);
    showDebugLine(`queue test: building ${EVENT_COUNT} test events`);
    const events = Array.from({ length: EVENT_COUNT }, (_, i) => testEvent(i));
    showDebugLine(`queue test: about ${(JSON.stringify(events).length / 1e6).toFixed(1)} MB as JSON`);
    const queueId = testUuid();
    const blocked = [];
    for (let n = 1; n <= SAVES; n += 1) {
        if (n > 1) {
            await wait(SAVE_EVERY_MS);
            events.push(testEvent(events.length));
            if (events.length > EVENT_COUNT)
                events.shift();
        }
        const result = await saveOnce(db, queueId, events);
        blocked.push(result.blockedMs);
        const clock = new Date().toISOString().slice(11, 23);
        showDebugLine(`${clock} queue save ${n}/${SAVES}: main thread ${Math.round(result.blockedMs)} ms, ` +
            `done in ${Math.round(result.totalMs)} ms`);
    }
    await deleteDebugData(db);
    const sorted = [...blocked].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
    const max = sorted[sorted.length - 1] ?? 0;
    showDebugLine(`queue test done: main thread median ${Math.round(median)} ms, max ${Math.round(max)} ms. Test data removed`);
}
/** Starts the queue save test once, only with ?debug=queue. */
export function startQueueSaveDebugOnce() {
    if (started || !debugFlagEnabled('queue'))
        return;
    started = true;
    void run().catch((error) => {
        showDebugLine(`queue test failed: ${error instanceof Error ? error.message : String(error)}`);
    });
}
//# sourceMappingURL=queue-debug.js.map